import Backbone from 'backbone';
import _ from 'underscore';
import { getActionText, getActionType, getActionTypeTextMapper, getDefaultActionType, mapActionType } from '@bingads-webui-campaign-legacy/adextension-actionlink/model/language-type-text-mapper';
import { getHotelCenterShared } from '@bingads-webui-campaign/hotel-center-shared';
import template from '@bingads-webui-campaign-legacy/adextension-actionlink/template/search-selector-adapter.pug';

const i18n = window.globalLegacyI18n.get('component/i18n/loader!campaignui/feature/adextensions');
const automatedTextValue = i18n.getString('ActionAdExtension_ActionText_Automated');

const maxOptions = 1024;
const { DropdownSearchSelector } = getHotelCenterShared();

function isMatch(src, dest) {
  if (_.isString(src) && _.isString(dest)) {
    return src.toLowerCase().indexOf(dest.toLowerCase()) >= 0;
  }
  return false;
}

function dataSource({
  searchString = '',
  Language,
  reserveActionType,
  isMultimediaAd,
  isDisplayPlusMMA,
  isBoostScenario,
  isBoostCustomCTAEnabled,
  isPmaxAd,
  isCustomCTAEnabled,
  isMMAAutomatedCTAOptInEnabled,
}) {
  let actionTypeText = getActionTypeTextMapper({ Language, reserveActionType });
  let customTextValue;

  if (((isMultimediaAd && !isMMAAutomatedCTAOptInEnabled) || isDisplayPlusMMA) && Language === 'None') {
    actionTypeText = {
      Automated: automatedTextValue,
    };
  }

  if (isBoostScenario && Language === 'None') {
    actionTypeText = {};
  }

  if (_.has(actionTypeText, 'CustomText')) {
    customTextValue = actionTypeText.CustomText;
    delete actionTypeText.CustomText;
  }
  const options = _.chain(actionTypeText)
    .map((value, id) => {
      if (_.isEmpty(searchString) || // no filter
        isMatch(value, searchString)) {
        return {
          id,
          value,
        };
      }
      return null;
    })
    .compact()
    .sortBy('value')
    .value();

  // add custom text option on top
  if (((isBoostScenario && isBoostCustomCTAEnabled) || isCustomCTAEnabled) && !_.isUndefined(customTextValue)) {
    options.unshift({
      id: 'CustomText',
      value: customTextValue,
    });
  }

  if (isPmaxAd) {
    options.unshift({
      id: 'Automated',
      value: automatedTextValue,
    });
  }

  return {
    totalCount: _.size(options),
    options,
  };
}

/* eslint new-cap:0 */
export default class View extends Backbone.View {
  initialize({ observable, viewModel, entity = null, field = null, isMultimediaAd = false, callback = undefined, isBoostScenario, isBoostCustomCTAEnabled = false, isPmaxAd = false, isDisplayPlusMMA = false, isCustomCTAEnabled = false, isMMAAutomatedCTAOptInEnabled = false, defaultActionText = '', noEntiyCheck = false}) {
    this.observable = observable;
    if (_.isEmpty(this.observable()) || this.observable() === 'Unknown') {
      if (!(isMultimediaAd || isBoostScenario || isPmaxAd)) {
        // init observable
        this.observable(getDefaultActionType({ Language: viewModel.Language() }));
      }
      if (isPmaxAd) { // init observable for pmax ad
        this.observable('Automated');
      }
    } else if (defaultActionText) {
      this.observable(getActionType({ Language: viewModel.Language(), text: defaultActionText }));
    } else { // map action type to new one
      this.observable(mapActionType(this.observable())); // will fallback to default if mapped action type not exist
    }
    this.viewModel = viewModel;
    this.isDisplayPlusMMA = isDisplayPlusMMA;
    this.isMultimediaAd = isMultimediaAd;
    this.isBoostScenario = isBoostScenario;
    this.isBoostCustomCTAEnabled = isBoostCustomCTAEnabled;
    this.isPmaxAd = isPmaxAd;
    this.isCustomCTAEnabled = isCustomCTAEnabled;
    this.defaultActionText = defaultActionText;
    this.isMMAAutomatedCTAOptInEnabled = isMMAAutomatedCTAOptInEnabled;

    if (this.isMultimediaAd && !noEntiyCheck) {
      if (!(entity instanceof Backbone.Model)) {
        throw new Error('entity should be a backbone model');
      }

      if (_.isEmpty(field)) {
        throw new Error('field is required');
      }

      this.field = field;
      this.entity = entity;
    }
    this.callback = callback;
  }

  setDropdownSearchSelector() {
    if (this.dropdownSearchSelector) {
      this.dropdownSearchSelector.remove();
    }
    const Language = this.viewModel.Language();
    // eslint-disable-next-line no-nested-ternary
    let defaultSelectId = '';

    if (this.defaultActionText) {
      const selectId = getActionType({ Language, text: this.defaultActionText });

      if (_.isEmpty(selectId) && this.isCustomCTAEnabled) {
        defaultSelectId = 'CustomText';
      } else if (_.isEmpty(selectId)) {
        defaultSelectId = getDefaultActionType({ Language });
      } else {
        defaultSelectId = selectId;
      }
    } else {
      defaultSelectId = getActionText({
        Language,
        ActionType: this.observable(),
      }) ? this.observable() : getDefaultActionType({ Language });
    }

    let selectOption = {
      id: defaultSelectId,
      value: getActionText({
        Language,
        ActionType: defaultSelectId,
      }),
    };

    if (((this.isMultimediaAd && !this.isMMAAutomatedCTAOptInEnabled) || this.isDisplayPlusMMA) && Language === 'None') {
      // set action to 'Automated' if language is 'None'
      selectOption = {
        id: 'Automated',
        value: automatedTextValue,
      };
    }

    if (this.isBoostScenario) {
      if (Language === 'None') {
        selectOption = {
          id: 'Default',
          value: i18n.getString('ActionAdExtension_ActionText_Placeholder'),
        };
      }
    }

    if (this.isPmaxAd && this.observable() === 'Automated') {
      selectOption = {
        id: 'Automated',
        value: automatedTextValue,
      };
    }

    this.dropdownSearchSelector = new DropdownSearchSelector({
      selectOption,
      dataSource: (arg) => dataSource(_.extend({}, arg, {
        Language,
        reserveActionType: defaultSelectId,
        isMultimediaAd: this.isMultimediaAd,
        isDisplayPlusMMA: this.isDisplayPlusMMA,
        isBoostScenario: this.isBoostScenario,
        isBoostCustomCTAEnabled: this.isBoostCustomCTAEnabled,
        isPmaxAd: this.isPmaxAd,
        isCustomCTAEnabled: this.isCustomCTAEnabled,
        isMMAAutomatedCTAOptInEnabled: this.isMMAAutomatedCTAOptInEnabled,
      })),
      pageSize: maxOptions,
      i18n,
      forceEnableSearch: true,
      invokeSearchOnChange: true,
      placeholder: i18n.getString('ActionAdExtension_ActionText_Search'),
      isDisabled: ((this.isMultimediaAd || this.isDisplayPlusMMA) && _.isEmpty(Language)) || (this.isBoostScenario && Language === 'None'),
    }).render();
    if (this.callback && selectOption) {
      this.callback(selectOption);
    }
    this.dropdownSearchSelector.on('option-selected', (selectedOption) => {
      this.observable(selectedOption.id);
      if (this.callback) {
        this.callback(selectedOption);
      }
    });
    this.$('.actionextension-search-dropdown-container').append(this.dropdownSearchSelector.el);
  }

  render() {
    this.$el.html(template());
    this.viewModel.Language.subscribe(() => {
      this.setDropdownSearchSelector();
    });
    this.setDropdownSearchSelector();

    return this;
  }
}
